import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';
import EventForm from '../components/forms/EventForm';

const EventFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: new Date().toISOString().slice(0, 16),
    endDate: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16),
    location: '',
    type: '',
    registrationUrl: '',
    imageUrl: '',
    imageFile: null,
    isPublished: true,
    removeImage: false 
  });

  const [submitting, setSubmitting] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState('');

  // Load event data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadEvent = async () => {
        try {
          const event = await adminAPI.getEventById(id);
          const imageUrl = event.image || event.imageUrl || '';
          const formattedEvent = {
            ...event,
            startDate: event.startDate ? event.startDate.slice(0, 16) : '',
            endDate: event.endDate ? event.endDate.slice(0, 16) : '',
            imageUrl: imageUrl,
            imageFile: null,
            location: event.venue || event.location || '',
            removeImage: false
          };
          setFormData(formattedEvent);
          setOriginalImageUrl(imageUrl);
        } catch (error) {
          console.error('Error loading event:', error);
          toast.error('Failed to load event data');
          navigate('/admin/dashboard');
        }
      };
      loadEvent();
    }
  }, [id, isEditMode, navigate]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    setSubmitting(true);

    try {
      // Determine if image was removed
      const imageWasRemoved = isEditMode && originalImageUrl && !formData.imageUrl && !formData.imageFile;

      // ALWAYS use FormData (consistent with backend expectations)
      const formDataToSend = new FormData();

      // Add file if exists
      if (formData.imageFile) {
        formDataToSend.append('file', formData.imageFile);
      }

      // Add remove_image flag if image was removed
      if (imageWasRemoved) {
        formDataToSend.append('remove_image', 'true');
      }

      // Add all required fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('type', formData.type);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('start_date', new Date(formData.startDate).toISOString());
      formDataToSend.append('end_date', new Date(formData.endDate).toISOString());

      // Add optional fields
      if (formData.location && formData.location.trim()) {
        formDataToSend.append('location', formData.location);
      }
      if (formData.registrationUrl && formData.registrationUrl.trim()) {
        formDataToSend.append('registration_url', formData.registrationUrl);
      }

      if (isEditMode) {
        await adminAPI.updateEvent(id, formDataToSend);
        toast.success('Event updated successfully');
      } else {
        await adminAPI.createEvent(formDataToSend);
        toast.success('Event created successfully');
      }

      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving event:', error);
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to save event';
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  return (
    <div className="container mx-auto p-6">
      <EventForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitting={submitting}
        isEdit={isEditMode}
      />
    </div>
  );
};

export default EventFormPage;